import { useCallback, useEffect, useMemo, useState } from "react";
import { Bell, Download, RefreshCw, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { DataTable } from "../Table";
import {
  exportAdminStockRequestsCsv,
  getAdminStockRequests,
  getAdminStockRequestStats,
} from "../../../services/api";
import { useNotification } from "../../../hooks/useNotification";
import StockRequestDrawer from "./StockRequestDrawer";
import StockNotificationsDrawer from "./StockNotificationsDrawer";
import "./StockRequestsView.css";

const PAGE_SIZE = 10;
const PAGE_SIZE_OPTIONS = [10, 20, 50];

/** Statuses that are still open (not yet rejected/cancelled/fulfilled). */
const ACTIVE_STATUSES = ["pending", "approved"];


const STATUS_FILTERS = [
  { key: "", statKey: "total" },
  { key: "pending" },
  { key: "approved" },
  { key: "rejected" },
  { key: "cancelled" },
];

const formatDateTime = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const totalUnits = (items = []) =>
  items.reduce((sum, item) => sum + (Number.parseInt(item.quantity, 10) || 0), 0);

export default function StockRequestsView({ user }) {
  const { t } = useTranslation();
  // Head promoter = a promoter with is_head_promoter, acting in Approver mode.
  const isHeadPromoter =
    user?.role === "promoter" && Boolean(user?.is_head_promoter);
  // Super admin and the head of promoter can save notification recipients;
  // admins can view only; OP cannot open notifications at all.
  const canEditNotifications = user?.role === "super_admin" || isHeadPromoter;
  const canManageNotifications =
    ["admin", "super_admin"].includes(user?.role) || isHeadPromoter;
  // Approve/reject is for admins/super-admins and the head of promoter — OP is
  // view/export only.
  const canReview =
    ["admin", "super_admin"].includes(user?.role) || isHeadPromoter;
  const { showError, showSuccess } = useNotification();

  // Default to the pending queue: that is the list that needs action.
  const [statusFilter, setStatusFilter] = useState("pending");
  const [requests, setRequests] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [refreshToken, setRefreshToken] = useState(0);
  const [pageLimit, setPageLimit] = useState(PAGE_SIZE);
  const [pageOffset, setPageOffset] = useState(0);
  const [activeRequestId, setActiveRequestId] = useState(null);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [exporting, setExporting] = useState(false);
  const [notificationsOpen, setNotificationsOpen] = useState(false);

  const refresh = useCallback(() => {
    setRefreshToken((previous) => previous + 1);
  }, []);

  useEffect(() => {
    let cancelled = false;

    const fetchRequests = async () => {
      try {
        setLoading(true);
        setError("");
        const response = await getAdminStockRequests({
          status: statusFilter || undefined,
          startDate: startDate || undefined,
          endDate: endDate || undefined,
          limit: pageLimit,
          offset: pageOffset,
        });

        if (cancelled) return;
        const rows = Array.isArray(response?.data) ? response.data : [];
        setRequests(rows);
        setTotalCount(
          Number.parseInt(response?.pagination?.total, 10) || rows.length || 0,
        );
      } catch (fetchError) {
        if (cancelled) return;
        setRequests([]);
        setTotalCount(0);
        const message =
          fetchError?.response?.data?.error ||
          t("admin.stockRequests.failedToLoad");
        setError(message);
        showError(message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchRequests();
    return () => {
      cancelled = true;
    };
  }, [statusFilter, startDate, endDate, pageLimit, pageOffset, refreshToken, showError, t]);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const response = await getAdminStockRequestStats();
        if (!cancelled) setStats(response?.data || null);
      } catch (statsError) {
        // Stats are supplementary — the queue still works without them.
        if (!cancelled) setStats(null);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [refreshToken]);

  const handleStatusFilter = (nextStatus) => {
    setStatusFilter(nextStatus);
    setPageOffset(0);
  };

  const handleDateChange = (setter) => (event) => {
    setter(event.target.value);
    setPageOffset(0);
  };

  const clearDateRange = () => {
    setStartDate("");
    setEndDate("");
    setPageOffset(0);
  };

/**
 * Export the current filters to CSV.
 *
 * A 204 means no matching data was found, so show a message instead of
 * downloading an empty file.
 */

  const handleExport = async () => {
    if (exporting) return;

    if (startDate && endDate && endDate < startDate) {
      showError(
        t("admin.stockRequests.invalidRange", {
          defaultValue: "The end date cannot be earlier than the start date.",
        }),
      );
      return;
    }

    try {
      setExporting(true);
      const response = await exportAdminStockRequestsCsv({
        startDate: startDate || undefined,
        endDate: endDate || undefined,
        status: statusFilter || undefined,
      });

      if (response.status === 204) {
        showError(t("admin.stockRequests.exportEmpty"));
        return;
      }

      // Prefer the server's filename so the date stamp stays consistent.
      const disposition = response.headers?.["content-disposition"] || "";
      const match = disposition.match(/filename="?([^"]+)"?/);
      const filename = match ? match[1] : "stock_requests.csv";

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      showSuccess(t("admin.stockRequests.exportSuccess"));
    } catch (exportError) {
      showError(
        exportError?.response?.data?.error ||
          t("admin.stockRequests.exportFailed"),
      );
    } finally {
      setExporting(false);
    }
  };

  const tableColumns = useMemo(
    () => [
      { label: t("admin.stockRequests.columns.request") },
      { label: t("admin.stockRequests.columns.promoter") },
      { label: t("admin.stockRequests.columns.outlet") },
      { label: t("admin.stockRequests.columns.items") },
      {
        label: t("admin.stockRequests.columns.requested"),
        headerClassName: "admin-stock-requests-requested-col",
      },
      {
        label: t("admin.stockRequests.columns.status"),
        headerClassName: "admin-stock-requests-status-col",
      },
      {
        label: t("admin.stockRequests.columns.actions"),
        headerClassName: "admin-stock-requests-actions-col",
      },
    ],
    [t],
  );

  const renderRow = (request) => {
    const items = Array.isArray(request.items) ? request.items : [];
    const units = totalUnits(items);
    const firstItem = items[0];
    const extraCount = items.length - 1;

    return (
      <tr key={request.stock_request_id}>
        <td>
          <div className="admin-stock-requests-cell-stack">
            <strong>{request.order_no || `#${request.stock_request_id}`}</strong>
            <span className="admin-stock-requests-muted">
              {request.warehouse
                ? [
                    request.warehouse.warehouse_code,
                    request.warehouse.warehouse_name,
                  ]
                    .filter(Boolean)
                    .join(" · ")
                : "-"}
            </span>
          </div>
        </td>
        <td>
          <div className="admin-stock-requests-cell-stack">
            <strong>{request.promoter?.full_name || "-"}</strong>
            <span className="admin-stock-requests-muted">
              {request.promoter?.email || ""}
            </span>
          </div>
        </td>
        <td>
          <div className="admin-stock-requests-cell-stack">
            <span>{request.outlet?.bp_outlet_name || "-"}</span>
          </div>
        </td>
        <td>
          <div className="admin-stock-requests-cell-stack">
            <span className="admin-stock-requests-items-summary">
              {firstItem
                ? firstItem.model_name || firstItem.product_name || "-"
                : "-"}
              {extraCount > 0 && (
                <em>
                  {" "}
                  {t("admin.stockRequests.plusMore", { count: extraCount })}
                </em>
              )}
            </span>
            {/* "2 products · 7 units" — plainer than the ERP term "lines". */}
            <span className="admin-stock-requests-muted">
              {t("admin.stockRequests.productCount", { count: items.length })}
              {" · "}
              {t("admin.stockRequests.unitCount", { count: units })}
            </span>
          </div>
        </td>
        <td>
          <span className="admin-stock-requests-muted">
            {formatDateTime(request.requested_at)}
          </span>
        </td>
        <td>
          <span
            className={`admin-stock-requests-badge is-${request.status}`}
            title={
              ACTIVE_STATUSES.includes(request.status)
                ? t("admin.stockRequests.holdsStock")
                : t("admin.stockRequests.releasedStock")
            }
          >
            {t(`admin.stockRequests.status.${request.status}`, {
              defaultValue: request.status,
            })}
          </span>
        </td>
        <td>
          <button
            type="button"
            className="admin-stock-requests-review-btn"
            onClick={() => setActiveRequestId(request.stock_request_id)}
          >
            {request.status === "pending" && canReview
              ? t("admin.stockRequests.review")
              : t("admin.stockRequests.view")}
          </button>
        </td>
      </tr>
    );
  };

  return (
    <div className="admin-stock-requests-page">
      <div className="admin-stock-requests-hero">
        <div>
          <h2 className="admin-section-title">
            {t("admin.stockRequests.title")}
          </h2>
          <p className="admin-section-subtitle">
            {t("admin.stockRequests.subtitle")}
          </p>
        </div>

        <div className="admin-stock-requests-hero-actions">
          {/* Date range applies to both the table and the export, so what you
              see is what you download. */}
          <div className="admin-stock-requests-range">
            <label htmlFor="stockRequestsStartDate">
              {t("admin.stockRequests.from")}
            </label>
            <input
              id="stockRequestsStartDate"
              type="date"
              value={startDate}
              max={endDate || undefined}
              onChange={handleDateChange(setStartDate)}
              className="admin-stock-requests-date"
            />
            <label htmlFor="stockRequestsEndDate">
              {t("admin.stockRequests.to")}
            </label>
            <input
              id="stockRequestsEndDate"
              type="date"
              value={endDate}
              min={startDate || undefined}
              onChange={handleDateChange(setEndDate)}
              className="admin-stock-requests-date"
            />
            {(startDate || endDate) && (
              <button
                type="button"
                className="admin-stock-requests-range-clear"
                onClick={clearDateRange}
                aria-label={t("admin.common.clear")}
                title={t("admin.common.clear")}
              >
                <X size={14} />
              </button>
            )}
          </div>

          <button
            type="button"
            className="admin-btn-secondary"
            onClick={handleExport}
            disabled={exporting || loading}
          >
            <Download size={16} />
            {exporting
              ? t("admin.stockRequests.exporting")
              : t("admin.stockRequests.exportCsv")}
          </button>

          {canManageNotifications && (
            <button
              type="button"
              className="admin-btn-secondary"
              onClick={() => setNotificationsOpen(true)}
            >
              <Bell size={16} />
              {t("admin.stockNotifications.manageButton")}
            </button>
          )}

          <button
            type="button"
            className="admin-btn-secondary"
            onClick={refresh}
            disabled={loading}
          >
            <RefreshCw size={16} />
            {t("admin.common.refresh")}
          </button>
        </div>
      </div>


      <div
        className="admin-stock-requests-filterbar"
        role="tablist"
        aria-label={t("admin.stockRequests.filterByStatus")}
      >
        {STATUS_FILTERS.map(({ key, statKey }) => {
          const isActive = statusFilter === key;
          const count = stats ? (stats[statKey || key] ?? 0) : null;
          return (
            <button
              key={key || "all"}
              type="button"
              role="tab"
              aria-selected={isActive}
              className={`admin-stock-requests-segment ${key ? `is-${key}` : "is-all"} ${
                isActive ? "is-active" : ""
              }`}
              onClick={() => handleStatusFilter(key)}
            >
              {key ? <span className="admin-stock-requests-dot" aria-hidden="true" /> : null}
              {t(`admin.stockRequests.status.${key || "all"}`)}
              {count !== null ? (
                <span className="admin-stock-requests-segment-count">{count}</span>
              ) : null}
            </button>
          );
        })}
      </div>

      <div className="admin-stock-requests-table-wrap">
        <DataTable
          data={requests}
          columns={tableColumns}
          renderRow={renderRow}
          showHeader={false}
          showSearch={false}
          showFilter={false}
          showHeaderActions={false}
          loading={loading}
          error={error}
          emptyMessage={
            statusFilter
              ? t("admin.stockRequests.emptyForStatus", {
                  status: t(`admin.stockRequests.status.${statusFilter}`),
                })
              : t("admin.stockRequests.empty")
          }
          serverMode
          serverPagination={{
            total: totalCount,
            limit: pageLimit,
            offset: pageOffset,
          }}
          onServerPaginationChange={({ limit, offset }) => {
            setPageLimit(limit);
            setPageOffset(offset);
          }}
          initialItemsPerPage={PAGE_SIZE}
          itemsPerPageOptions={PAGE_SIZE_OPTIONS}
          tableClassName="admin-stock-requests-table admin-users-table"
          containerClassName="admin-stock-requests-table-card admin-users-section"
        />
      </div>

      <StockRequestDrawer
        isOpen={Boolean(activeRequestId)}
        stockRequestId={activeRequestId}
        canReview={canReview}
        onClose={() => setActiveRequestId(null)}
        onReviewed={(message) => {
          showSuccess(message);
          setActiveRequestId(null);
          refresh();
        }}
        t={t}
        showError={showError}
      />

      <StockNotificationsDrawer
        isOpen={notificationsOpen}
        onClose={() => setNotificationsOpen(false)}
        canEdit={canEditNotifications}
        t={t}
        showError={showError}
        showSuccess={showSuccess}
      />
    </div>
  );
}
